import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceMasterPolicyListItem } from './insurance-api.service';
import {
  INSURANCE_LIST_TOOLBAR_STYLES,
  SortOrder,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeInsuranceOperationsLiveUpdates,
  toggleSortField
} from './insurance-list-page.utils';

type MasterPolicySortField = 'masterPolicyNumber' | 'status' | 'billingMode' | 'activeMemberCount' | 'effectiveFrom';

@Component({
  standalone: true,
  selector: 'ain-insurance-master-policies-shell',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    FormsModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.group.masterPolicies.title')"
      [description]="t('insurance.group.masterPolicies.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.masterPolicies.help.page.title'"
        [descriptionKey]="'insurance.masterPolicies.help.page.description'"
        [bulletKeys]="[
          'insurance.masterPolicies.help.page.bullets.scope',
          'insurance.masterPolicies.help.page.bullets.members',
          'insurance.masterPolicies.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.group.masterPolicies.actions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.masterPolicies.help.listFilters.title'"
                [descriptionKey]="'insurance.masterPolicies.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.masterPolicies.help.listFilters.bullets.search',
                  'insurance.masterPolicies.help.listFilters.bullets.status',
                  'insurance.masterPolicies.help.listFilters.bullets.billingMode'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.group.masterPolicies.filters.status') }}</span>
                <p-select
                  [options]="statusOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.group.masterPolicies.filters.billingMode') }}</span>
                <p-select
                  [options]="billingModeOptions"
                  optionValue="value"
                  [(ngModel)]="billingModeFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.group.masterPolicies.states.empty')" [message]="t('insurance.group.masterPolicies.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('masterPolicyNumber')">{{ t('insurance.group.masterPolicies.fields.masterPolicyNumber') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('status')">{{ t('insurance.group.masterPolicies.fields.status') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('billingMode')">{{ t('insurance.group.masterPolicies.fields.billingMode') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('activeMemberCount')">{{ t('insurance.group.masterPolicies.fields.activeMemberCount') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('effectiveFrom')">{{ t('insurance.group.masterPolicies.fields.effectiveFrom') }}</button></th>
                  <th>{{ t('insurance.group.masterPolicies.fields.effectiveTo') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.masterPolicyNumber }}</button></td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ t(billingModeKey(row.billingMode)) }}</td>
                  <td>{{ row.activeMemberCount }}</td>
                  <td>{{ row.effectiveFrom | ainDate }}</td>
                  <td>{{ row.effectiveTo | ainDate }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
    ${INSURANCE_LIST_TOOLBAR_STYLES}
  `]
})
export class InsuranceMasterPoliciesShellComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly info = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceMasterPolicyListItem[]>([]);
  readonly selectedRows = signal<InsuranceMasterPolicyListItem[]>([]);
  readonly search = signal('');
  readonly sortField = signal<MasterPolicySortField>('masterPolicyNumber');
  readonly sortOrder = signal<SortOrder>(1);
  statusFilter: string | null = null;
  billingModeFilter: string | null = null;

  liveStatusKey = liveStatusKey;

  readonly statusOptions = [
    { value: 'Draft', labelKey: 'insurance.masterPolicies.status.draft' },
    { value: 'Active', labelKey: 'insurance.masterPolicies.status.active' },
    { value: 'Suspended', labelKey: 'insurance.masterPolicies.status.suspended' },
    { value: 'Cancelled', labelKey: 'insurance.masterPolicies.status.cancelled' },
    { value: 'Expired', labelKey: 'insurance.masterPolicies.status.expired' }
  ];

  readonly billingModeOptions = [
    { value: 'MasterBilled', labelKey: 'insurance.masterPolicies.billingMode.masterBilled' },
    { value: 'MemberBilled', labelKey: 'insurance.masterPolicies.billingMode.memberBilled' }
  ];

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.listMasterPolicies().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.permissionDenied.set(false);
        this.error.set('');
        this.info.set('');
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceMasterPolicyListItem[] {
    const value = this.search().trim().toLowerCase();
    const filtered = this.rows()
      .filter(row => !value || row.masterPolicyNumber.toLowerCase().includes(value) || row.status.toLowerCase().includes(value))
      .filter(row => !this.statusFilter || row.status === this.statusFilter)
      .filter(row => !this.billingModeFilter || row.billingMode === this.billingModeFilter);

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field], order));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: InsuranceMasterPolicyListItem[] | InsuranceMasterPolicyListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/master-policies', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/master-policies/new']);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'insurance-master-policies.csv',
      ['MasterPolicyNumber', 'Status', 'BillingMode', 'ActiveMemberCount', 'EffectiveFrom', 'EffectiveTo'],
      rows.map(item => [
        item.masterPolicyNumber,
        item.status,
        item.billingMode,
        item.activeMemberCount.toString(),
        item.effectiveFrom,
        item.effectiveTo
      ]));
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: MasterPolicySortField): void {
    const next = toggleSortField(field, this.sortField(), this.sortOrder());
    this.sortField.set(next.field);
    this.sortOrder.set(next.order);
  }

  statusKey(status: string): string {
    return `insurance.masterPolicies.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  billingModeKey(mode: string): string {
    return `insurance.masterPolicies.billingMode.${mode.charAt(0).toLowerCase()}${mode.slice(1)}`;
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const live = await subscribeInsuranceOperationsLiveUpdates(tenantId, this.liveUpdateService, () => this.load());
    this.liveUnsubscribe = live.unsubscribe;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
